if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Prioritize cart-drawer to open the drawer when adding to cart from product page
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Ensure cart element is found, especially if it was initialized later
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
        if (this.cart && typeof this.cart.getSectionsToRender !== 'function') {
          let upgradeTimer;
          try {
            await Promise.race([
              customElements.whenDefined(this.cart.localName),
              new Promise((_, reject) => { upgradeTimer = setTimeout(() => reject(new Error('Cart component is unavailable')), 3000); }),
            ]);
            customElements.upgrade(this.cart);
          } finally { clearTimeout(upgradeTimer); }
          if (typeof this.cart.getSectionsToRender !== 'function') throw new Error('Cart component did not initialize');
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Get delivery estimate from the product page display
        const deliveryElement = document.getElementById('delivery-text');
        if (deliveryElement) {
          let deliveryText = deliveryElement.innerText || deliveryElement.textContent || '';
          if (deliveryText) {
            // Extract just the estimate part (remove "Estimate Shipping Time: " prefix)
            deliveryText = deliveryText.replace('Estimate Shipping Time: ', '').replace(', Est ', ' ');
            formData.append('properties[Estimated Delivery]', deliveryText);
            console.log('Delivery estimate added to cart:', deliveryText);
          } else {
            console.warn('Delivery element found but no text content');
          }
        } else {
          console.warn('Delivery element #delivery-text not found');
        }
        
        for (const [key, value] of Array.from(formData.entries())) {
          const property = /^properties\[(.+)\]$/.exec(key);
          if (!property || property[1].startsWith('_') || typeof value !== 'string') continue;
          if (value.trim()) formData.set(key, value.trim());
          else formData.delete(key);
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        await fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            console.log('[DEBUG] cart/add response:', response);
            console.log('[DEBUG] this.cart is:', this.cart);
            if (response.status) {
              console.log('[DEBUG] Error branch — response.status was truthy:', response.status);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const hasPerf = typeof CartPerformance !== 'undefined';
            const startMarker = hasPerf ? CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;
            if (!this.error) {
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  if (hasPerf) CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                }).catch((err) => {
                  console.error('[CartDrawer] A cartUpdate subscriber threw an error (drawer will still open):', err);
                });
              } catch (err) {
                console.error('[CartDrawer] publish() threw synchronously (drawer will still open):', err);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (hasPerf) {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (hasPerf) {
              console.log('[DEBUG] Calling renderContents via CartPerformance.measure');
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            } else {
              console.log('[DEBUG] Calling renderContents directly');
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('[DEBUG] fetch/add caught an error:', e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (typeof CartPerformance !== 'undefined') {
              CartPerformance.measureFromEvent("add:user-action", evt);
            }
          });
        } catch (error) {
          console.error('[GBJ Add to cart]', error);
          this.handleErrorMessage('Cart could not start. Please refresh and try again.');
        } finally {
          this.submitButton.classList.remove('loading');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
